import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge } from '../components/StatusBadge';
import RoomIntelligenceSection from '../components/RoomIntelligenceSection';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  ArrowLeft, DoorOpen, Box, Copy, ClipboardList, AlertTriangle, ChevronRight 
} from 'lucide-react';
import { toast } from 'sonner';

export default function RoomDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRTL = i18n.language === 'ar';
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRoomOverview(); }, [id]);

  const loadRoomOverview = async () => {
    try {
      const { data } = await api.get(`/rooms/${id}/overview`);
      setData(data);
    } catch (err) {
      toast.error(t('toast.error'));
      navigate('/rooms');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('assetDetail.copied'));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;
  }

  if (!data) return null;

  const { room, kpis, assets, open_work_orders } = data;

  return (
    <div data-testid="room-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/rooms?tab=rooms')} className="shrink-0" data-testid="room-back-btn">
          <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
        </Button>
        
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <DoorOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{room.name}</h1>
              <p className="text-sm text-muted-foreground">{room.branch_name}</p>
            </div>
          </div>
          
          {/* Room Code with Copy */}
          {room.code && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-sm px-3 py-1">
                {t('roomDetail.code')}: {room.code}
              </Badge>
              <button 
                onClick={() => copyToClipboard(room.code)} 
                className="p-1.5 hover:bg-muted rounded-md transition-colors"
                title={t('assetDetail.copyId')}
              >
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-card border-border shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Box className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-barlow font-bold text-brand">{kpis.total_assets || 0}</p>
                <p className="text-xs text-muted-foreground">{t('common.assets')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-card border-border shadow-sm ${kpis.open_work_orders > 0 ? 'border-amber-200 dark:border-amber-900/50' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className={`text-2xl font-barlow font-bold ${kpis.open_work_orders > 0 ? 'text-amber-500' : 'text-brand'}`}>{kpis.open_work_orders || 0}</p>
                <p className="text-xs text-muted-foreground">{t('roomDetail.openIssues')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-card border-border shadow-sm ${kpis.overdue_work_orders > 0 ? 'border-rose-200 dark:border-rose-900/50' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <p className={`text-2xl font-barlow font-bold ${kpis.overdue_work_orders > 0 ? 'text-rose-500' : 'text-brand'}`}>{kpis.overdue_work_orders || 0}</p>
                <p className="text-xs text-muted-foreground">{t('roomDetail.overdueIssues')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assets in Room */}
        <div className="lg:col-span-2">
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                <Box className="w-5 h-5 text-purple-500" />
                {t('roomDetail.assetsInRoom')} ({assets.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {assets.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">{t('roomDetail.noAssets')}</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>{t('common.name')}</TableHead>
                        <TableHead>{t('assetDetail.serial')}</TableHead>
                        <TableHead>{t('assets.category')}</TableHead>
                        <TableHead className="text-center">{t('roomDetail.openIssues')}</TableHead>
                        <TableHead className="text-center">{t('roomDetail.overdueIssues')}</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assets.map((asset) => (
                        <TableRow 
                          key={asset.id} 
                          className="hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/assets/${asset.id}`)}
                          data-testid={`asset-row-${asset.id}`}
                        >
                          <TableCell className="font-medium text-foreground">{asset.name}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">{asset.serial || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{asset.category || '—'}</TableCell>
                          <TableCell className="text-center">
                            {asset.open_issues > 0 ? (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                {asset.open_issues}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {asset.overdue_issues > 0 ? (
                              <Badge variant="destructive">{asset.overdue_issues}</Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className={`w-4 h-4 text-muted-foreground ${isRTL ? 'rotate-180' : ''}`} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Open Work Orders */}
        <div>
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-amber-500" />
                {t('roomDetail.openWOs')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {open_work_orders.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">{t('roomDetail.noOpenWOs')}</div>
              ) : (
                <div className="divide-y divide-border">
                  {open_work_orders.map((wo) => (
                    <div 
                      key={wo.id}
                      className="px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                      data-testid={`wo-row-${wo.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-xs text-muted-foreground">#{wo.id.slice(0, 8)}</span>
                        <StatusBadge status={wo.status} />
                      </div>
                      {wo.asset_name && (
                        <p className="text-sm text-foreground">{wo.asset_name}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={wo.type === 'preventive' ? 'default' : 'outline'} className="text-xs">
                          {wo.type === 'preventive' ? t('workOrder.preventive') : t('workOrder.corrective')}
                        </Badge>
                        {wo.assigned_to_name && (
                          <span className="text-xs text-muted-foreground">{wo.assigned_to_name}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Room Info */}
          <Card className="bg-card border-border shadow-sm mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground">{t('roomDetail.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('common.name')}</span>
                  <span className="text-foreground font-medium">{room.name}</span>
                </div>
                {room.code && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('roomDetail.code')}</span>
                    <span className="text-foreground font-mono">{room.code}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('common.branch')}</span>
                  <span className="text-foreground">{room.branch_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('common.status')}</span>
                  <span className="text-foreground">{room.status || 'active'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Room Intelligence Section */}
          <div className="mt-6">
            <RoomIntelligenceSection roomId={room.id} />
          </div>
        </div>
      </div>
    </div>
  );
}
